import { useRouter } from 'next/router';
import { type MouseEvent, memo, useCallback, useEffect } from 'react';

import { cn, tailwindVariants } from '@air/utils-tailwind';

import { MainSideNavItemCredits } from '~/components/v2/components/workspace/main-side-nav/items/credits/MainSideNavItemCredits';
import { MainSideNavItemAccount } from '~/components/v2/components/workspace/main-side-nav/items/MainSideNavItemAccount';
import { MainSideNavItemAutomations } from '~/components/v2/components/workspace/main-side-nav/items/MainSideNavItemAutomations';
import { MainSideNavItemBoards } from '~/components/v2/components/workspace/main-side-nav/items/MainSideNavItemBoards';
import { MainSideNavItemCanvases } from '~/components/v2/components/workspace/main-side-nav/items/MainSideNavItemCanvases';
import { MainSideNavItemContext } from '~/components/v2/components/workspace/main-side-nav/items/MainSideNavItemContext';
import { MainSideNavItemHelp } from '~/components/v2/components/workspace/main-side-nav/items/MainSideNavItemHelp';
import { MainSideNavItemImports } from '~/components/v2/components/workspace/main-side-nav/items/MainSideNavItemImports';
import { MainSideNavItemLibraries } from '~/components/v2/components/workspace/main-side-nav/items/MainSideNavItemLibraries';
import { MainSideNavItemNewAtAir } from '~/components/v2/components/workspace/main-side-nav/items/MainSideNavItemNewAtAir';
import { MainSideNavItemNotifications } from '~/components/v2/components/workspace/main-side-nav/items/MainSideNavItemNotifications';
import { MainSideNavItemPerformance } from '~/components/v2/components/workspace/main-side-nav/items/MainSideNavItemPerformance';
import { MainSideNavItemRecentlyDeleted } from '~/components/v2/components/workspace/main-side-nav/items/MainSideNavItemRecentlyDeleted';
import { MainSideNavItemSettings } from '~/components/v2/components/workspace/main-side-nav/items/MainSideNavItemSettings';
import { MainSideNavItemShowcases } from '~/components/v2/components/workspace/main-side-nav/items/MainSideNavItemShowcases';
import { MainSideNavItemSkills } from '~/components/v2/components/workspace/main-side-nav/items/MainSideNavItemSkills';
import { MainSideNavItemUploads } from '~/components/v2/components/workspace/main-side-nav/items/MainSideNavItemUploads';
import { MainSideNavPinButton } from '~/components/v2/components/workspace/main-side-nav/items/MainSideNavPinButton';
import { MainSideNavItemHoverExpandProvider } from '~/components/v2/components/workspace/main-side-nav/MainSideNavItemHoverExpandContext';
import { MainSideNavSection } from '~/components/v2/components/workspace/main-side-nav/MainSideNavSection';
import { useMainSideNavOverlayExpand } from '~/components/v2/components/workspace/main-side-nav/useMainSideNavOverlayExpand';
import { useSyncElementWidthCssVariable } from '~/components/v2/hooks/useSyncElementWidthCssVariable';
import { useMainSideNavStore } from '~/components/v2/store/features/workspace/useMainSideNavStore';

/** In-flow rail width. The upload dropzones offset by this so they start after the rail. */
export const MAIN_SIDE_NAV_WIDTH_CSS_VARIABLE = '--main-side-nav-width';

type MainSideNavRailState = 'collapsed' | 'expanded' | 'pinned';

const getMainSideNavRailState = ({
  isOverlayExpanded,
  isPinned,
}: {
  isOverlayExpanded: boolean;
  isPinned: boolean;
}): MainSideNavRailState => {
  if (isPinned) {
    return 'pinned';
  }

  return isOverlayExpanded ? 'expanded' : 'collapsed';
};

const mainSideNavRail = tailwindVariants({
  base: 'absolute inset-y-0 left-0 z-50 flex h-full flex-col overflow-hidden ads:border-default/80 border-gray-4/80 border-r ads:bg-base bg-gray-1 pt-3 transition-[width,box-shadow,border-radius] duration-200 ease-out motion-reduce:transition-none',
  variants: {
    state: {
      collapsed:
        'w-[60px] shadow-none has-[[data-state=open]]:w-58 has-[[data-state=open]]:rounded-r-12 has-[[data-state=open]]:shadow-popover',
      expanded: 'w-58 rounded-r-12 shadow-popover has-[[data-state=open]]:shadow-popover',
      pinned: 'w-58',
    },
  },
});

export const MainSideNav = memo(() => {
  const { pathname } = useRouter();
  const isPinnedStore = useMainSideNavStore((state) => state.pinned);
  const suppressPinned = useMainSideNavStore((state) => state.suppressPinned);
  const isPinned = isPinnedStore && !suppressPinned;
  const activeOverlay = useMainSideNavStore((state) => state.activeOverlay);
  const setActiveOverlay = useMainSideNavStore((state) => state.setActiveOverlay);
  const {
    expandOnItemHover,
    isOverlayExpanded,
    onBlur: handleBlur,
    onClick: handleExpandClick,
    onFocus: handleFocus,
    onMouseLeave: handleMouseLeave,
  } = useMainSideNavOverlayExpand({
    hasOpenMainSideNavOverlay: activeOverlay !== null,
    isPinned,
  });

  const handleClick = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      handleExpandClick(event);

      if (!(event.target instanceof Element)) {
        return;
      }

      // Portaled overlay content (credits popover, help menu) is not a DOM child of
      // the nav, but React still bubbles the click here. Leave those overlays open.
      if (!event.currentTarget.contains(event.target)) {
        return;
      }

      if (event.target.closest('[data-main-side-nav-overlay]')) {
        return;
      }

      setActiveOverlay(null);
    },
    [handleExpandClick, setActiveOverlay],
  );

  useEffect(() => {
    return () => {
      useMainSideNavStore.getState().setActiveOverlay(null);
    };
  }, []);

  const navRailState = getMainSideNavRailState({ isOverlayExpanded, isPinned });
  // Hover-expand is an absolute overlay and does not change this box. Pin does.
  const { syncElementWidthRef } = useSyncElementWidthCssVariable({
    cssVariable: MAIN_SIDE_NAV_WIDTH_CSS_VARIABLE,
    getCssValue: (widthPx) => `${widthPx}px`,
  });

  return (
    <MainSideNavItemHoverExpandProvider expandOnItemHover={expandOnItemHover}>
      <div
        className={cn(
          // No z-index on this wrapper: it would trap the page scrim in a stacking context above the notifications popover.
          'group/main-side-nav relative h-full shrink-0 transition-[width] duration-200 ease-out motion-reduce:transition-none',
          isPinned ? 'w-58' : 'w-[60px]',
        )}
        data-hover-expand={isOverlayExpanded ? true : undefined}
        data-pinned={isPinned ? true : undefined}
        data-testid="MAIN_SIDE_NAV_ROOT"
        ref={syncElementWidthRef}
      >
        {/* Keyboard expand uses nav focus; mouse expand uses item hover. Dropdowns use data-state=open because menu content is portaled. */}
        {/* biome-ignore lint/a11y/useKeyWithClickEvents: overlay collapse on click is pointer-only */}
        <nav // NOSONAR -- pointer click collapses overlay; keyboard expand/collapse is onFocus/onBlur
          aria-label="Main navigation"
          className={mainSideNavRail({ state: navRailState })}
          data-main-side-nav
          data-testid="MAIN_SIDE_NAV"
          onBlur={handleBlur}
          onClick={handleClick}
          onFocus={handleFocus}
          onMouseLeave={handleMouseLeave}
        >
          {/* Inner columns stay at expanded width so labels layout at full size; the nav clips them. */}
          <div className="scrollbar scrollbar-track-gray-2 scrollbar-thumb-gray-7 [&:not(:hover)]:scrollbar-track-transparent flex min-h-0 w-58 grow flex-col overflow-y-auto overflow-x-hidden overscroll-contain">
            <section
              aria-label="Work"
              className="flex flex-col"
              data-section-id="work"
              data-testid="MAIN_SIDE_NAV_SECTION"
            >
              <MainSideNavItemLibraries pathname={pathname} />
              <MainSideNavItemBoards pathname={pathname} />
              <MainSideNavItemCanvases pathname={pathname} />
              <MainSideNavItemNotifications />
            </section>
            <MainSideNavSection aria-label="Agent" id="agent">
              <MainSideNavItemContext pathname={pathname} />
              <MainSideNavItemSkills pathname={pathname} />
              <MainSideNavItemAutomations pathname={pathname} />
            </MainSideNavSection>
            <MainSideNavSection aria-label="Tools" id="tools">
              <MainSideNavItemPerformance pathname={pathname} />
              <MainSideNavItemShowcases pathname={pathname} />
            </MainSideNavSection>
          </div>
          <section
            aria-label="Workspace"
            className="flex w-58 shrink-0 flex-col gap-0.5 py-2.5"
            data-section-id="workspace"
            data-testid="MAIN_SIDE_NAV_SECTION"
          >
            <div className="flex flex-col">
              <MainSideNavItemImports />
              <MainSideNavItemUploads />
              <MainSideNavItemRecentlyDeleted pathname={pathname} />
              <MainSideNavItemCredits />
              <MainSideNavItemNewAtAir />
              <MainSideNavItemHelp />
              <MainSideNavItemSettings pathname={pathname} />
            </div>
            <div className="grid h-9 grid-cols-[minmax(0,1fr)_auto] items-center gap-2 pr-3">
              <MainSideNavItemAccount />
              <MainSideNavPinButton />
            </div>
          </section>
        </nav>
      </div>
    </MainSideNavItemHoverExpandProvider>
  );
});

MainSideNav.displayName = 'MainSideNav';
